import React, { useState, useEffect } from 'react';
import { formatTimeAgo } from './utils';
import api from './api';

const StarRating = ({ rating, onRate, size = 24 }) => {
  return (
    <div style={{ display: 'flex', gap: '4px' }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <span
          key={star}
          onClick={onRate ? () => onRate(star) : undefined}
          style={{
            cursor: onRate ? 'pointer' : 'default',
            fontSize: `${size}px`,
            color: star <= rating ? '#FF8A3D' : '#1E1E28',
            transition: 'color 0.15s ease',
          }}
        >
          {'\u2605'}
        </span>
      ))}
    </div>
  );
};

const RaceReviews = ({ raceId, user, onLoginRequired }) => {
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWriteReview, setShowWriteReview] = useState(false);
  const [myReview, setMyReview] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);

  // Form state
  const [overallRating, setOverallRating] = useState(0);
  const [courseRating, setCourseRating] = useState(0);
  const [organizationRating, setOrganizationRating] = useState(0);
  const [crowdRating, setCrowdRating] = useState(0);
  const [valueRating, setValueRating] = useState(0);
  const [reviewText, setReviewText] = useState('');
  const [wouldRunAgain, setWouldRunAgain] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchReviews();
    if (user) {
      checkRegistration();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [raceId, user]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const [reviewsRes, summaryRes] = await Promise.all([
        api.get(`/races/${raceId}/reviews`),
        api.get(`/races/${raceId}/reviews/summary`),
      ]);
      setReviews(reviewsRes.data);
      setSummary(summaryRes.data);

      if (user) {
        const mine = reviewsRes.data.find(r => r.user_id === user.id);
        setMyReview(mine || null);
      }
    } catch (err) {
      console.error('Error loading reviews:', err);
    } finally {
      setLoading(false);
    }
  };

  const checkRegistration = async () => {
    try {
      const res = await api.get('/registrations');
      const registered = res.data.some(r => r.raceId === raceId);
      setIsRegistered(registered);
    } catch (err) {
      // ignore
    }
  };

  const openWriteModal = (review = null) => {
    if (review) {
      setOverallRating(review.overall_rating);
      setCourseRating(review.course_rating || 0);
      setOrganizationRating(review.organization_rating || 0);
      setCrowdRating(review.crowd_rating || 0);
      setValueRating(review.value_rating || 0);
      setReviewText(review.review_text || '');
      setWouldRunAgain(review.would_run_again);
    } else {
      setOverallRating(0);
      setCourseRating(0);
      setOrganizationRating(0);
      setCrowdRating(0);
      setValueRating(0);
      setReviewText('');
      setWouldRunAgain(null);
    }
    setError('');
    setShowWriteReview(true);
  };

  const handleSubmit = async () => {
    if (overallRating === 0) {
      setError('Please select an overall rating');
      return;
    }

    setSubmitting(true);
    setError('');

    const payload = {
      overall_rating: overallRating,
      course_rating: courseRating || null,
      organization_rating: organizationRating || null,
      crowd_rating: crowdRating || null,
      value_rating: valueRating || null,
      review_text: reviewText,
      would_run_again: wouldRunAgain,
    };

    try {
      if (myReview) {
        await api.put(`/reviews/${myReview.id}`, payload);
      } else {
        await api.post(`/races/${raceId}/reviews`, payload);
      }
      setShowWriteReview(false);
      fetchReviews();
    } catch (err) {
      const msg = err.response?.data?.error || 'Failed to submit review';
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!myReview) return;
    try {
      await api.delete(`/reviews/${myReview.id}`);
      setMyReview(null);
      fetchReviews();
    } catch (err) {
      console.error('Delete review error:', err);
    }
  };

  if (loading) {
    return <p style={{ color: '#8b9cb3', textAlign: 'center', padding: '2rem' }}>Loading reviews...</p>;
  }

  return (
    <div>
      {/* Summary Bar */}
      {summary && summary.review_count > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '2rem',
          padding: '1.5rem',
          background: 'rgba(255, 255, 255, 0.05)',
          border: '1px solid rgba(255, 138, 61, 0.2)',
          borderRadius: '12px',
          marginBottom: '1.5rem',
          flexWrap: 'wrap',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '2rem', fontWeight: 700, color: '#FF8A3D' }}>
              {summary.avg_overall}
            </span>
            <div>
              <StarRating rating={Math.round(parseFloat(summary.avg_overall))} size={18} />
              <p style={{ color: '#8b9cb3', fontSize: '0.8rem', margin: '0.2rem 0 0' }}>
                {summary.review_count} {summary.review_count === 1 ? 'review' : 'reviews'}
              </p>
            </div>
          </div>

          {summary.would_run_again_pct != null && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              padding: '0.5rem 1rem',
              background: 'rgba(74, 222, 128, 0.1)',
              borderRadius: '20px',
            }}>
              <span style={{ color: '#4ade80', fontWeight: 600, fontSize: '0.9rem' }}>
                {summary.would_run_again_pct}%
              </span>
              <span style={{ color: '#8b9cb3', fontSize: '0.8rem' }}>would run again</span>
            </div>
          )}

          {[
            { label: 'Course', val: summary.avg_course },
            { label: 'Organization', val: summary.avg_organization },
            { label: 'Crowd', val: summary.avg_crowd },
            { label: 'Value', val: summary.avg_value },
          ].filter(c => c.val != null).map(cat => (
            <div key={cat.label} style={{ textAlign: 'center' }}>
              <p style={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.95rem', margin: 0 }}>{cat.val}</p>
              <p style={{ color: '#8b9cb3', fontSize: '0.75rem', margin: '0.15rem 0 0' }}>{cat.label}</p>
            </div>
          ))}
        </div>
      )}

      {/* Write / Edit Review Button */}
      <div style={{ marginBottom: '1.5rem' }}>
        {!user ? (
          <button
            onClick={() => onLoginRequired && onLoginRequired()}
            style={{
              padding: '0.75rem 1.5rem',
              background: '#FF8A3D',
              color: '#0B0B10',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: '0.95rem',
            }}
          >
            Sign in to write a review
          </button>
        ) : !isRegistered ? (
          <p style={{ color: '#8b9cb3', fontSize: '0.9rem' }}>
            Register for this race to leave a review.
          </p>
        ) : (
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={() => openWriteModal(myReview)}
              style={{
                padding: '0.75rem 1.5rem',
                background: '#FF8A3D',
                color: '#0B0B10',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: 600,
                fontSize: '0.95rem',
              }}
            >
              {myReview ? 'Edit Your Review' : 'Write a Review'}
            </button>
            {myReview && (
              <button
                onClick={handleDelete}
                style={{
                  padding: '0.75rem 1rem',
                  background: 'transparent',
                  color: '#ff6b6b',
                  border: '1px solid rgba(255, 107, 107, 0.3)',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontSize: '0.85rem',
                }}
              >
                Delete
              </button>
            )}
          </div>
        )}
      </div>

      {/* Reviews List */}
      {reviews.length === 0 ? (
        <div style={{
          textAlign: 'center',
          padding: '3rem',
          background: 'rgba(255, 255, 255, 0.03)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 138, 61, 0.15)',
        }}>
          <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>{'⭐'}</div>
          <h3 style={{ color: '#FFFFFF', marginBottom: '0.5rem' }}>No reviews yet</h3>
          <p style={{ color: '#9ca3af' }}>Be the first to review this race!</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {reviews.map((review) => {
            const name = review.author_first_name
              ? `${review.author_first_name}`
              : review.author_name || 'Runner';
            const initials = review.author_first_name
              ? review.author_first_name[0].toUpperCase()
              : (review.author_name || 'R')[0].toUpperCase();

            return (
              <div
                key={review.id}
                style={{
                  background: review.user_id === user?.id ? 'rgba(255, 138, 61, 0.08)' : 'rgba(255, 255, 255, 0.03)',
                  border: review.user_id === user?.id ? '1px solid rgba(255, 138, 61, 0.3)' : '1px solid rgba(255, 138, 61, 0.1)',
                  borderRadius: '12px',
                  padding: '1.25rem',
                }}
              >
                {/* Author Row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  {review.author_photo ? (
                    <img src={review.author_photo} alt={`${review.author_first_name || review.author_name || 'Reviewer'} avatar`} style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover' }} />
                  ) : (
                    <div style={{
                      width: '36px', height: '36px', borderRadius: '50%',
                      background: 'rgba(255, 138, 61, 0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.85rem', fontWeight: 700, color: '#FF8A3D',
                    }}>
                      {initials}
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span style={{ color: '#FFFFFF', fontWeight: 600, fontSize: '0.9rem' }}>{name}</span>
                      {review.author_verified && (
                        <span style={{ color: '#4ade80', fontSize: '0.7rem', fontWeight: 600, background: 'rgba(74, 222, 128, 0.15)', padding: '0.1rem 0.4rem', borderRadius: '8px' }}>
                          {'\u2713'} Verified
                        </span>
                      )}
                      {review.user_id === user?.id && (
                        <span style={{ color: '#FF8A3D', fontSize: '0.7rem', fontWeight: 600, background: 'rgba(255, 138, 61, 0.15)', padding: '0.1rem 0.4rem', borderRadius: '8px' }}>
                          You
                        </span>
                      )}
                    </div>
                    <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>{formatTimeAgo(review.created_at)}</span>
                  </div>
                </div>

                {/* Overall Rating */}
                <div style={{ marginBottom: '0.5rem' }}>
                  <StarRating rating={review.overall_rating} size={18} />
                </div>

                {/* Sub-ratings */}
                {(review.course_rating || review.organization_rating || review.crowd_rating || review.value_rating) && (
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.75rem', flexWrap: 'wrap' }}>
                    {[
                      { label: 'Course', val: review.course_rating },
                      { label: 'Organization', val: review.organization_rating },
                      { label: 'Crowd', val: review.crowd_rating },
                      { label: 'Value', val: review.value_rating },
                    ].filter(c => c.val).map(cat => (
                      <span key={cat.label} style={{ color: '#8b9cb3', fontSize: '0.8rem' }}>
                        {cat.label}: <span style={{ color: '#FF8A3D', fontWeight: 600 }}>{cat.val}</span>
                      </span>
                    ))}
                  </div>
                )}

                {/* Review Text */}
                {review.review_text && (
                  <p style={{ color: '#d1d5db', fontSize: '0.95rem', lineHeight: 1.6, margin: '0.5rem 0' }}>
                    {review.review_text}
                  </p>
                )}

                {/* Would Run Again */}
                {review.would_run_again != null && (
                  <div style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '0.35rem',
                    marginTop: '0.5rem',
                    padding: '0.3rem 0.75rem',
                    borderRadius: '16px',
                    background: review.would_run_again ? 'rgba(74, 222, 128, 0.1)' : 'rgba(255, 107, 107, 0.1)',
                    fontSize: '0.8rem',
                    color: review.would_run_again ? '#4ade80' : '#ff6b6b',
                    fontWeight: 500,
                  }}>
                    {review.would_run_again ? 'Would run again' : 'Would not run again'}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Write Review Modal */}
      {showWriteReview && (
        <div
          style={{
            position: 'fixed',
            top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0, 0, 0, 0.85)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
          }}
          onClick={() => setShowWriteReview(false)}
        >
          <div
            style={{
              background: '#0B0B10',
              border: '1px solid rgba(255, 138, 61, 0.3)',
              borderRadius: '20px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              maxHeight: '85vh',
              overflowY: 'auto',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ color: '#FFFFFF', marginBottom: '1.5rem', fontSize: '1.3rem' }}>
              {myReview ? 'Edit Your Review' : 'Write a Review'}
            </h2>

            {error && (
              <div style={{
                background: 'rgba(255, 107, 107, 0.1)',
                border: '1px solid rgba(255, 107, 107, 0.3)',
                borderRadius: '8px',
                padding: '0.75rem',
                marginBottom: '1rem',
                color: '#ff6b6b',
                fontSize: '0.9rem',
              }}>
                {error}
              </div>
            )}

            {/* Rating Categories */}
            {[
              { label: 'Overall Rating *', value: overallRating, setter: setOverallRating },
              { label: 'Course', value: courseRating, setter: setCourseRating },
              { label: 'Organization', value: organizationRating, setter: setOrganizationRating },
              { label: 'Crowd Support', value: crowdRating, setter: setCrowdRating },
              { label: 'Value', value: valueRating, setter: setValueRating },
            ].map((cat) => (
              <div key={cat.label} style={{ marginBottom: '1rem' }}>
                <label style={{ color: '#9ca3af', fontSize: '0.85rem', display: 'block', marginBottom: '0.35rem' }}>
                  {cat.label}
                </label>
                <StarRating rating={cat.value} onRate={cat.setter} size={28} />
              </div>
            ))}

            {/* Review Text */}
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ color: '#9ca3af', fontSize: '0.85rem', display: 'block', marginBottom: '0.35rem' }}>
                Your Review
              </label>
              <textarea
                value={reviewText}
                onChange={(e) => setReviewText(e.target.value)}
                placeholder="Share your experience..."
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  background: '#16161D',
                  border: '1px solid rgba(255, 138, 61, 0.2)',
                  borderRadius: '8px',
                  color: '#FFFFFF',
                  fontSize: '0.95rem',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Would Run Again */}
            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ color: '#9ca3af', fontSize: '0.85rem', display: 'block', marginBottom: '0.5rem' }}>
                Would you run this race again?
              </label>
              <div style={{ display: 'flex', gap: '0.75rem' }}>
                {[
                  { label: 'Yes', value: true },
                  { label: 'No', value: false },
                ].map((opt) => (
                  <button
                    key={opt.label}
                    onClick={() => setWouldRunAgain(wouldRunAgain === opt.value ? null : opt.value)}
                    style={{
                      padding: '0.5rem 1.25rem',
                      background: wouldRunAgain === opt.value
                        ? (opt.value ? 'rgba(74, 222, 128, 0.2)' : 'rgba(255, 107, 107, 0.2)')
                        : 'transparent',
                      border: `1px solid ${wouldRunAgain === opt.value
                        ? (opt.value ? '#4ade80' : '#ff6b6b')
                        : 'rgba(139, 156, 179, 0.3)'}`,
                      color: wouldRunAgain === opt.value
                        ? (opt.value ? '#4ade80' : '#ff6b6b')
                        : '#8b9cb3',
                      borderRadius: '20px',
                      cursor: 'pointer',
                      fontSize: '0.9rem',
                      fontWeight: 500,
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Submit */}
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <button
                onClick={() => setShowWriteReview(false)}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: 'transparent',
                  border: '1px solid rgba(255, 138, 61, 0.3)',
                  color: '#8b9cb3',
                  borderRadius: '10px',
                  cursor: 'pointer',
                  fontSize: '0.95rem',
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || overallRating === 0}
                style={{
                  flex: 1,
                  padding: '0.75rem',
                  background: overallRating === 0 ? '#4a5568' : '#FF8A3D',
                  border: 'none',
                  color: overallRating === 0 ? '#8b9cb3' : '#0B0B10',
                  borderRadius: '10px',
                  cursor: overallRating === 0 ? 'not-allowed' : 'pointer',
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? 'Submitting...' : (myReview ? 'Update Review' : 'Submit Review')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RaceReviews;
